import React, { type ReactNode } from 'react';
import { graphql, useFragment } from 'react-relay';
import type { IssueKey } from '@atlassian/jira-shared-types/src/general.tsx';
import type { selectedView_issueNavigator_SelectedViewContainer_issueSearchView$key as IssueSearchViewFragment } from '@atlassian/jira-relay/src/__generated__/selectedView_issueNavigator_SelectedViewContainer_issueSearchView.graphql';
import type { selectedView_issueNavigator_SelectedViewContainer_jiraView$key as JiraViewFragment } from '@atlassian/jira-relay/src/__generated__/selectedView_issueNavigator_SelectedViewContainer_jiraView.graphql';
import { isRefactorNinToViewSchemaEnabled } from '@atlassian/jira-issue-navigator-rollout/src/is-refactor-nin-to-view-schema-enabled.tsx';
import { DEFAULT_VIEW_ID } from '../../common/constants.tsx';
import type { IssueNavigatorViewId } from '../../common/types.tsx';
import { parseIssueNavigatorViewIdOrDefault } from '../../common/utils/index.tsx';
import { Container as SelectedViewStateContainer } from '../../controllers/selected-view-state/index.tsx';
import { useIssueSearchActions } from '../../services/issue-search/selectors.tsx';

export type Props = {
	children: ReactNode;
	issueSearchView: IssueSearchViewFragment | null;
	jiraView: JiraViewFragment | null;
	/**
	 * Event emitted when the selected view is updated. The caller may want to specify the issue which should be
	 * selected by default when the view is changed, in which case they can provide an `issueKey` argument.
	 *
	 * @param view View that was updated
	 * @param issueKey Optional issue key that should be selected
	 */
	onSetView: (view: IssueNavigatorViewId, issueKey?: IssueKey) => void;
};

export const SelectedViewContainer = ({
	children,
	issueSearchView,
	jiraView,
	onSetView,
}: Props) => {
	const { onChangeView: onIssueSearchForView } = useIssueSearchActions();

	const issueSearchViewData = useFragment(
		graphql`
			fragment selectedView_issueNavigator_SelectedViewContainer_issueSearchView on JiraIssueSearchView {
				viewId
			}
		`,
		issueSearchView,
	);

	const jiraViewData = useFragment(
		graphql`
			fragment selectedView_issueNavigator_SelectedViewContainer_jiraView on JiraView {
				... on JiraIssueSearchViewMetadata {
					viewId
				}
			}
		`,
		jiraView,
	);

	const viewId = parseIssueNavigatorViewIdOrDefault(
		isRefactorNinToViewSchemaEnabled() ? jiraViewData?.viewId : issueSearchViewData?.viewId,
		DEFAULT_VIEW_ID,
	);

	return (
		<SelectedViewStateContainer
			view={viewId}
			onSetView={onSetView}
			onIssueSearchForView={onIssueSearchForView}
		>
			{children}
		</SelectedViewStateContainer>
	);
};
